import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProject } from '../contexts/ProjectContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import FilePublisher from './FilePublisher';

const { FiFolder, FiPlus, FiTrash2, FiFile, FiCode, FiFolderPlus, FiEdit2, FiUpload, FiMove, FiCopy, FiGlobe } = FiIcons;

const FileExplorer = () => {
  const { files, activeFile, setActiveFile, createFile, deleteFile, createFolder, renameFile, moveFile, duplicateFile } = useProject();
  const [showNewFile, setShowNewFile] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showFilePublisher, setShowFilePublisher] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedExtension, setSelectedExtension] = useState('html');
  const [renamingFile, setRenamingFile] = useState(null);
  const [newFileNameRename, setNewFileNameRename] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [draggedFile, setDraggedFile] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const fileExtensions = [
    { ext: 'html', name: 'HTML File', icon: FiCode, color: 'text-orange-400' },
    { ext: 'css', name: 'CSS File', icon: FiCode, color: 'text-blue-400' },
    { ext: 'js', name: 'JavaScript File', icon: FiCode, color: 'text-yellow-400' },
    { ext: 'jsx', name: 'React JSX File', icon: FiCode, color: 'text-cyan-400' },
    { ext: 'ts', name: 'TypeScript File', icon: FiCode, color: 'text-blue-500' },
    { ext: 'tsx', name: 'TypeScript React File', icon: FiCode, color: 'text-blue-500' },
    { ext: 'json', name: 'JSON File', icon: FiFile, color: 'text-green-400' },
    { ext: 'md', name: 'Markdown File', icon: FiFile, color: 'text-purple-400' },
    { ext: 'txt', name: 'Text File', icon: FiFile, color: 'text-gray-400' },
    { ext: 'xml', name: 'XML File', icon: FiCode, color: 'text-red-400' },
    { ext: 'svg', name: 'SVG File', icon: FiCode, color: 'text-pink-400' },
    { ext: 'php', name: 'PHP File', icon: FiCode, color: 'text-indigo-400' },
    { ext: 'py', name: 'Python File', icon: FiCode, color: 'text-green-500' },
    { ext: 'java', name: 'Java File', icon: FiCode, color: 'text-red-500' },
    { ext: 'cpp', name: 'C++ File', icon: FiCode, color: 'text-blue-600' },
    { ext: 'c', name: 'C File', icon: FiCode, color: 'text-gray-500' },
    { ext: 'scss', name: 'SCSS File', icon: FiCode, color: 'text-pink-500' },
    { ext: 'sass', name: 'SASS File', icon: FiCode, color: 'text-pink-600' },
    { ext: 'vue', name: 'Vue File', icon: FiCode, color: 'text-emerald-400' },
    { ext: 'yaml', name: 'YAML File', icon: FiFile, color: 'text-orange-500' },
    { ext: 'yml', name: 'YML File', icon: FiFile, color: 'text-orange-500' },
    { ext: 'ini', name: 'INI File', icon: FiFile, color: 'text-gray-400' },
    { ext: 'env', name: 'Environment File', icon: FiFile, color: 'text-yellow-500' },
  ];

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCreateFile = () => {
    if (newFileName.trim()) {
      const fullFileName = selectedExtension ? `${newFileName.trim()}.${selectedExtension}` : newFileName.trim();
      createFile(fullFileName);
      setNewFileName('');
      setShowNewFile(false);
    }
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      createFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolder(false);
    }
  };

  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    uploadedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        createFile(file.name, e.target.result);
      };
      reader.readAsText(file);
    });
    event.target.value = ''; // Reset file input
  };

  const handleRenameFile = (oldPath, newName) => {
    if (newName.trim() && newName !== oldPath) {
      renameFile(oldPath, newName.trim());
      setRenamingFile(null);
      setNewFileNameRename('');
    }
  };

  const handleDuplicateFile = (e, filePath) => {
    e.stopPropagation();
    duplicateFile(filePath);
  };

  const startRenaming = (filePath) => {
    setRenamingFile(filePath);
    setNewFileNameRename(filePath);
  };

  const handleContextMenu = (e, filePath) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      filePath
    });
  };

  const getFileIcon = (filename) => {
    if (filename.endsWith('/')) return FiFolder;
    const ext = filename.split('.').pop()?.toLowerCase();
    const fileType = fileExtensions.find(f => f.ext === ext);
    return fileType ? fileType.icon : FiFile;
  };

  const getFileColor = (filename) => {
    if (filename.endsWith('/')) return 'text-yellow-400';
    const ext = filename.split('.').pop()?.toLowerCase();
    const fileType = fileExtensions.find(f => f.ext === ext);
    return fileType ? fileType.color : 'text-gray-400';
  };

  const toggleFolder = (folderPath) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, filePath) => {
    setDraggedFile(filePath);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', filePath);
  };

  const handleDragOver = (e, folderPath) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderPath);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverFolder(null);
  };

  const handleDrop = (e, targetFolder) => {
    e.preventDefault();
    e.stopPropagation();

    const sourceFile = draggedFile;
    if (sourceFile && targetFolder !== null && sourceFile !== targetFolder) {
      // Get the filename without path
      const fileName = sourceFile.split('/').pop();
      const newPath = targetFolder === 'root' ? fileName : `${targetFolder}/${fileName}`;

      // Don't move if it's the same path
      if (newPath !== sourceFile) {
        moveFile(sourceFile, newPath);
      }
    }

    setDraggedFile(null);
    setDragOverFolder(null);
  };

  const organizeFiles = () => {
    const organized = { folders: {}, files: [] };

    Object.keys(files).forEach(path => {
      if (path.includes('/')) {
        const parts = path.split('/');
        const folderName = parts[0];
        const remainingPath = parts.slice(1).join('/');

        if (!organized.folders[folderName]) {
          organized.folders[folderName] = {};
        }
        organized.folders[folderName][remainingPath] = files[path];
      } else {
        organized.files.push(path);
      }
    });

    return organized;
  };

  const renderFileTree = (fileObj, basePath = '') => {
    const organized = typeof fileObj === 'object' && !Array.isArray(fileObj) ? fileObj : organizeFiles();

    return (
      <>
        {/* Render folders */}
        {Object.keys(organized.folders || {}).map(folderName => {
          const folderPath = basePath ? `${basePath}/${folderName}` : folderName;
          const isExpanded = expandedFolders.has(folderPath);
          const isDragOver = dragOverFolder === folderPath;

          return (
            <div key={folderPath}>
              <motion.div
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group text-gray-300 hover:bg-gray-800/50 transition-all duration-200 ${
                  isDragOver ? 'bg-blue-500/20 border-2 border-dashed border-blue-400' : ''
                }`}
                onClick={() => toggleFolder(folderPath)}
                onDragOver={(e) => handleDragOver(e, folderPath)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folderPath)}
                layout
              >
                <div className="flex items-center flex-1 min-w-0">
                  <SafeIcon icon={FiFolder} className={`mr-3 ${isExpanded ? 'text-yellow-400' : 'text-gray-500'}`} />
                  <span className="truncate text-sm font-medium">{folderName}/</span>
                  {isDragOver && (
                    <span className="ml-2 text-xs text-blue-400 font-medium">Drop here</span>
                  )}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                  <SafeIcon icon={FiMove} className="text-gray-500 text-xs mr-1" title="Drag to move files" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFile(folderPath);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 rounded transition-all duration-200"
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                  </button>
                </div>
              </motion.div>

              {isExpanded && (
                <div className="ml-6 border-l border-gray-700 pl-2">
                  {Object.keys(organized.folders[folderName]).map(subPath => {
                    const fullPath = `${folderPath}/${subPath}`;
                    const isDragging = draggedFile === fullPath;

                    return (
                      <motion.div
                        key={fullPath}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group transition-all duration-200 ${
                          activeFile === fullPath
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : 'text-gray-300 hover:bg-gray-800/50'
                        } ${isDragging ? 'opacity-50' : ''}`}
                        onClick={() => setActiveFile(fullPath)}
                        onContextMenu={(e) => handleContextMenu(e, fullPath)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, fullPath)}
                        layout
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <SafeIcon icon={getFileIcon(subPath)} className={`mr-3 ${getFileColor(subPath)}`} />
                          {renamingFile === fullPath ? (
                            <input
                              type="text"
                              value={newFileNameRename}
                              onChange={(e) => setNewFileNameRename(e.target.value)}
                              onBlur={() => handleRenameFile(fullPath, newFileNameRename)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleRenameFile(fullPath, newFileNameRename);
                                } else if (e.key === 'Escape') {
                                  setRenamingFile(null);
                                }
                              }}
                              className="bg-gray-700 text-white px-1 rounded flex-1 text-sm"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span className="truncate text-sm font-medium">{subPath}</span>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                          <SafeIcon icon={FiMove} className="text-gray-500 text-xs" title="Drag to move" />
                          <button
                            onClick={(e) => handleDuplicateFile(e, fullPath)}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/10 p-1 rounded transition-all duration-200"
                            title="Duplicate File"
                          >
                            <SafeIcon icon={FiCopy} className="text-xs" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startRenaming(fullPath);
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-1 rounded transition-all duration-200"
                          >
                            <SafeIcon icon={FiEdit2} className="text-xs" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFile(fullPath);
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 rounded transition-all duration-200"
                          >
                            <SafeIcon icon={FiTrash2} className="text-xs" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Root drop zone */}
        <div
          className={`${organized.files.length === 0 ? 'h-8' : 'h-2'} transition-all duration-200 ${
            dragOverFolder === 'root' ? 'bg-blue-500/20 border-2 border-dashed border-blue-400 rounded-lg' : ''
          }`}
          onDragOver={(e) => handleDragOver(e, 'root')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'root')}
        >
          {dragOverFolder === 'root' && (
            <div className="flex items-center justify-center h-full text-blue-400 text-xs font-medium">
              Drop to move to root folder
            </div>
          )}
        </div>

        {/* Render files */}
        {(organized.files || []).map((filename) => {
          const isDragging = draggedFile === filename;

          return (
            <motion.div
              key={filename}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer group transition-all duration-200 ${
                activeFile === filename
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-gray-300 hover:bg-gray-800/50'
              } ${isDragging ? 'opacity-50' : ''}`}
              onClick={() => setActiveFile(filename)}
              onContextMenu={(e) => handleContextMenu(e, filename)}
              draggable
              onDragStart={(e) => handleDragStart(e, filename)}
              whileHover={{ x: 2 }}
              layout
            >
              <div className="flex items-center flex-1 min-w-0">
                <SafeIcon icon={getFileIcon(filename)} className={`mr-3 ${getFileColor(filename)}`} />
                {renamingFile === filename ? (
                  <input
                    type="text"
                    value={newFileNameRename}
                    onChange={(e) => setNewFileNameRename(e.target.value)}
                    onBlur={() => handleRenameFile(filename, newFileNameRename)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameFile(filename, newFileNameRename);
                      } else if (e.key === 'Escape') {
                        setRenamingFile(null);
                      }
                    }}
                    className="bg-gray-700 text-white px-1 rounded flex-1 text-sm"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="truncate text-sm font-medium">{filename}</span>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                <SafeIcon icon={FiMove} className="text-gray-500 text-xs" title="Drag to move" />
                <button
                  onClick={(e) => handleDuplicateFile(e, filename)}
                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10 p-1 rounded transition-all duration-200"
                  title="Duplicate File"
                >
                  <SafeIcon icon={FiCopy} className="text-xs" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startRenaming(filename);
                  }}
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-1 rounded transition-all duration-200"
                >
                  <SafeIcon icon={FiEdit2} className="text-xs" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFile(filename);
                  }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 rounded transition-all duration-200"
                >
                  <SafeIcon icon={FiTrash2} className="text-xs" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-950">
      {/* Header */}
      <div className="p-6 border-b border-gray-800 flex items-center justify-between">
        <h3 className="text-white font-semibold text-lg flex items-center">
          <SafeIcon icon={FiFolder} className="mr-3 text-blue-400" />
          Explorer
        </h3>
        <div className="flex gap-2">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            accept="*/*"
          />
          <label
            htmlFor="file-upload"
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 cursor-pointer"
            title="Upload Files"
          >
            <SafeIcon icon={FiUpload} className="text-lg" />
          </label>
          <button
            onClick={() => setShowNewFolder(true)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
            title="New Folder"
          >
            <SafeIcon icon={FiFolderPlus} className="text-lg" />
          </button>
          <button
            onClick={() => setShowNewFile(true)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
            title="New File"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
          </button>
          {/* Publish Button - Prominent but Clean */}
          <button
            onClick={() => setShowFilePublisher(true)}
            className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors duration-200 animate-pulse"
            title="🚀 Publish Files to Netlify"
          >
            <SafeIcon icon={FiGlobe} className="text-lg" />
          </button>
        </div>
      </div>

      {/* File List */}
      <div className="flex-1 overflow-y-auto">
        {Object.keys(files).length === 0 ? (
          <div className="p-6 text-gray-500 text-center">
            <SafeIcon icon={FiFolder} className="text-4xl mb-4 mx-auto text-gray-600" />
            <p className="text-sm">No files yet</p>
            <p className="text-xs mt-1">Create your first file to get started</p>
          </div>
        ) : (
          <div className="p-3">
            <div className="mb-2 text-xs text-gray-500 px-2">
              💡 Right-click files for more options • Drag files to move them between folders
            </div>
            {renderFileTree()}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <motion.div
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 py-2 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <button
            onClick={() => {
              setActiveFile(contextMenu.filePath);
              setShowFilePublisher(true);
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center"
          >
            <SafeIcon icon={FiGlobe} className="mr-2" />
            Publish File
          </button>
          <button
            onClick={() => {
              handleDuplicateFile({ stopPropagation: () => {} }, contextMenu.filePath);
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center"
          >
            <SafeIcon icon={FiCopy} className="mr-2" />
            Duplicate
          </button>
          <button
            onClick={() => {
              startRenaming(contextMenu.filePath);
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center"
          >
            <SafeIcon icon={FiEdit2} className="mr-2" />
            Rename
          </button>
          <hr className="border-gray-700 my-1" />
          <button
            onClick={() => {
              deleteFile(contextMenu.filePath);
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors flex items-center"
          >
            <SafeIcon icon={FiTrash2} className="mr-2" />
            Delete
          </button>
        </motion.div>
      )}

      {/* New File Modal */}
      <AnimatePresence>
        {showNewFile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-96 shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <h4 className="text-white font-semibold text-lg mb-4">Create New File</h4>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">File Type</label>
                <select
                  value={selectedExtension}
                  onChange={(e) => setSelectedExtension(e.target.value)}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-white text-sm"
                >
                  {fileExtensions.map((fileType) => (
                    <option key={fileType.ext} value={fileType.ext}>
                      {fileType.name} (.{fileType.ext})
                    </option>
                  ))}
                  <option value="">Custom Extension</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  File Name {selectedExtension && `(.${selectedExtension})`}
                </label>
                <input
                  type="text"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  placeholder={selectedExtension ? "filename" : "filename.ext"}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFile()}
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCreateFile}
                  disabled={!newFileName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                >
                  Create File
                </button>
                <button
                  onClick={() => {
                    setShowNewFile(false);
                    setNewFileName('');
                    setSelectedExtension('html');
                  }}
                  className="px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* New Folder Modal */}
      <AnimatePresence>
        {showNewFolder && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-96 shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <h4 className="text-white font-semibold text-lg mb-4">Create New Folder</h4>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="folder-name"
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-white"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                autoFocus
              />
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                >
                  Create Folder
                </button>
                <button
                  onClick={() => {
                    setShowNewFolder(false);
                    setNewFolderName('');
                  }}
                  className="px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* File Publisher Modal */}
      {showFilePublisher && (
        <FilePublisher onClose={() => setShowFilePublisher(false)} />
      )}
    </div>
  );
};

export default FileExplorer;